import React, { useEffect, useRef, useState } from "react";

const VimeoIframe = ({
  src,
  style,
  title,
  id,
  resizeDimensions,
  dimensions,
  setVideoData,
  actionCableVideoMontior,
  ...props
}) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const videoId = src?.split("/")[4]?.split("?")[0] || "";
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    console.log("[Vimeo] ===== VIMEO IFRAME INITIALIZING =====");
    console.log("[Vimeo] videoId:", videoId);
    console.log("[Vimeo] element id:", id);
    console.log("[Vimeo] hasCallback:", !!actionCableVideoMontior);
    
    if (!videoId) {
      console.error("[Vimeo] Invalid videoId:", videoId);
      return;
    }

    if (!actionCableVideoMontior) {
      console.warn("[Vimeo]   actionCableVideoMontior callback not provided");
    }

    let timeOut;
    const onVimeoIframeAPIReady = () => {
      console.log("[Vimeo] ===== VIMEO API READY =====");
      console.log("[Vimeo] iframeRef.current:", !!iframeRef.current);
      console.log("[Vimeo] window.Vimeo:", !!window.Vimeo);
      console.log("[Vimeo] window.Vimeo.Player:", !!(window.Vimeo && window.Vimeo.Player));
      
      if (
        iframeRef.current &&
        window.Vimeo &&
        window.Vimeo.Player
      ) {
        try {
          const player = new window.Vimeo.Player(iframeRef.current);
          playerRef.current = player;
          
          console.log("[Vimeo]    Player created for element:", id);
          
          // Store player on iframe for cleanup
          if (iframeRef.current) {
            iframeRef.current.player = player;
          }

          player.on("play", function () {
            console.log("[Vimeo] PLAY EVENT FIRED");
            console.log("[Vimeo] Element id:", id);
            setIsPlaying(true);
            
            // Trigger click event when video starts playing (similar to YouTube)
            if (props.onClick && typeof props.onClick === 'function') {
              try {
                const syntheticEvent = {
                  target: { id: id },
                  currentTarget: { id: id },
                  type: 'play',
                  stopPropagation: () => {},
                  preventDefault: () => {},
                  delegateTarget: { children: [] }
                };
                console.log("[Vimeo] Triggering onClick/elemClick for video play");
                props.onClick(syntheticEvent);
              } catch (e) {
                console.error("[Vimeo] Error triggering onClick:", e);
              }
            }
          });

          player.on("pause", function () {
            console.log("[Vimeo] Video paused for element:", id);
            setIsPlaying(false);
          });

          player.on("timeupdate", function (event) {
            // CYOA viewport check
            if (window?.objInit?.tlb?.disable_settings?.disable_on === "cyoa") {
              const rect = iframeRef.current.getBoundingClientRect();
              const isInsideViewport =
                rect.top < window.innerHeight &&
                rect.bottom > 0 &&
                rect.left < window.innerWidth &&
                rect.right > 0;

              if (!isInsideViewport) {
                player.pause();
              }
            }

            // Fix: event.percent is already 0-1, just multiply by 100 once
            const watchedPercent = Math.round(event.percent * 100);
            const watchedSeconds = Math.floor(event.seconds);
            const duration = Math.floor(event.duration);

            // Ensure minimum values if video is playing
            const finalWatchedSeconds = event.seconds > 0 ? Math.max(1, watchedSeconds) : 0;
            const finalWatchedPercent = event.seconds > 0 && watchedPercent === 0 ? 1 : watchedPercent;

            console.log("[Vimeo] Video tracking:", {
              id,
              seconds: event.seconds.toFixed(2),
              duration: duration,
              watchedSeconds: finalWatchedSeconds,
              watchedPercent: finalWatchedPercent
            });

            if (actionCableVideoMontior) {
              actionCableVideoMontior({
                id: id,
                watchedPercent: finalWatchedPercent,
                watchedSeconds: finalWatchedSeconds,
                duration: duration,
                isPlaying: true,
              });
            }
          });

          // Handle video ended event
          player.on("ended", function () {
            console.log("[Vimeo]    Video ended for element:", id);
            setIsPlaying(false);
            
            // Send final 100% watched data
            player.getDuration().then((duration) => {
              if (actionCableVideoMontior) {
                actionCableVideoMontior({
                  id: id,
                  watchedPercent: 100,
                  watchedSeconds: Math.floor(duration),
                  duration: Math.floor(duration),
                  isPlaying: false,
                });
              }
            }).catch((error) => {
              console.warn("[Vimeo] Error getting duration on ended:", error);
            });
          });

          // Optional: Set video data if callback provided
          if (setVideoData) {
            player.getVideoTitle().then((title) => {
              setVideoData({ title, id });
            }).catch((error) => {
              console.warn("[Vimeo] Error getting video title:", error);
            });
          }
        } catch (error) {
          console.error("[Vimeo] Error initializing player:", error);
        }
      } else {
        console.warn("[Vimeo]   Cannot create player:", {
          hasIframe: !!iframeRef.current,
          hasVimeo: !!window.Vimeo,
          hasPlayer: !!(window.Vimeo && window.Vimeo.Player)
        });
      }
    };

    // Wait a bit for iframe to load
    timeOut = setTimeout(() => {
      onVimeoIframeAPIReady();
    }, 300);

    return () => {
      console.log("[Vimeo] Cleaning up for element:", id);
      clearTimeout(timeOut);
      
      // Clean up event listeners
      if (playerRef.current) {
        try {
          playerRef.current.off("play");
          playerRef.current.off("pause");
          playerRef.current.off("timeupdate");
          playerRef.current.off("ended");
        } catch (e) {
          console.warn("[Vimeo] Error removing event listeners:", e);
        }
        playerRef.current = null;
      }
    };
  }, [videoId]); // Removed isPlaying from dependencies to prevent re-initialization

  return (
    <iframe
      onClick={props.onClick}
      ref={iframeRef}
      style={style}
      src={src}
      title={title}
      id={id}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      {...props}
    ></iframe>
  );
};

export default VimeoIframe;
